import type { AxiosError, AxiosResponse } from "axios";
import type {
  CreateSkillsBody,
  MoveSkillBody,
  Skill,
  SkillCategory,
  SkillsBulkImportResult,
  SkillsBulkImportRowError,
  SkillsBulkImportSuccess,
  SkillsListPage,
  SkillsListResponse,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

// -----------------------------------------------------------------------------
// Backend response shapes (snake_case) — kept local to this file
// -----------------------------------------------------------------------------

interface BackendSkill {
  mapping_id: string;
  name: string;
  category_id: string;
  order: number;
  created_at: string;
  updated_at?: string;
}

interface BackendCategory {
  mapping_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  skill_count: number;
  skills?: BackendSkill[];
}

interface BackendListResponseFull {
  success: boolean;
  statusCode: number;
  message: string;
  /** Total category count across all pages (for infinite pagination). */
  total?: number;
  page?: number;
  per_page?: number;
  data: BackendCategory[];
}

// -----------------------------------------------------------------------------
// Mapping helpers (backend → frontend)
// -----------------------------------------------------------------------------

const mapSkill = (skill: BackendSkill): Skill => ({
  id: skill.mapping_id,
  name: skill.name,
  categoryId: skill.category_id,
  workRoleCount: 0,
  createdAt: skill.created_at,
  updatedAt: skill.updated_at ?? skill.created_at,
});

const mapCategory = (cat: BackendCategory): SkillCategory => ({
  id: cat.mapping_id,
  name: cat.name,
  isSystem: false,
  skillCount: cat.skill_count ?? cat.skills?.length ?? 0,
  createdAt: cat.created_at,
  updatedAt: cat.updated_at ?? cat.created_at,
});

const buildListResponse = (
  categories: BackendCategory[],
): SkillsListResponse => {
  const skillsByCategoryId: Record<string, Skill[]> = {};
  for (const cat of categories) {
    skillsByCategoryId[cat.mapping_id] = (cat.skills ?? []).map(mapSkill);
  }
  return {
    categories: categories.map(mapCategory),
    skillsByCategoryId,
  };
};

// -----------------------------------------------------------------------------
// GET /v1/skills/categories — list all categories with nested skills
// Accepts optional `searchQuery` — when present, backend returns only the
// categories/skills that match (substring, case-insensitive). When a category
// name matches, ALL its skills are returned; when only a skill name matches,
// that category includes only the matching skills. Filtering happens server-
// side so search continues to work once category pagination is introduced.
// -----------------------------------------------------------------------------

export const getAllSkills = async (
  searchQuery?: string,
  page?: number,
  perPage?: number,
): Promise<SkillsListPage | undefined> => {
  try {
    const trimmed = searchQuery?.trim();
    const response = await axiosClient.get<BackendListResponseFull>(
      "/v1/skills/categories",
      {
        params: {
          ...(trimmed ? { searchQuery: trimmed } : {}),
          ...(page ? { page } : {}),
          ...(perPage ? { per_page: perPage } : {}),
        },
      },
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      const built = buildListResponse(resData.data ?? []);
      return {
        ...built,
        total: resData.total ?? built.categories.length,
        page: resData.page ?? page ?? 1,
        perPage: resData.per_page ?? perPage ?? built.categories.length,
      };
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// POST /v1/skills/categories — create category only (no skills)
// -----------------------------------------------------------------------------

export const createCategoryApi = async (name: string) => {
  try {
    const response = await axiosClient.post("/v1/skills/categories", { name });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// POST /v1/skills — create skills in existing category OR with new category
// -----------------------------------------------------------------------------

export const createSkillsApi = async (body: CreateSkillsBody) => {
  try {
    const response = await axiosClient.post("/v1/skills", body);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// PATCH /v1/skills/:skill_id — rename and/or reorder a single skill
// -----------------------------------------------------------------------------

interface UpdateSkillBody {
  name?: string;
  order?: number;
  /**
   * Optional full reordered list of skills in the same category. When sent,
   * backend bulk-updates orders atomically (use for drag-reorder).
   */
  skills?: Array<{ skill_id: string; order: number }>;
}

export const updateSkillApi = async (
  skillId: string,
  body: UpdateSkillBody,
) => {
  try {
    const response = await axiosClient.patch(`/v1/skills/${skillId}`, body);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// PATCH /v1/skills/categories/:category_id — rename category
// -----------------------------------------------------------------------------

export const updateCategoryApi = async (categoryId: string, name: string) => {
  try {
    const response = await axiosClient.patch(
      `/v1/skills/categories/${categoryId}`,
      { name },
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// PATCH /v1/skills/:skill_id/move — cross-category move
// -----------------------------------------------------------------------------

export const moveSkillApi = async (skillId: string, body: MoveSkillBody) => {
  try {
    const response = await axiosClient.patch(
      `/v1/skills/${skillId}/move`,
      body,
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// DELETE /v1/skills — single or bulk skill delete
// -----------------------------------------------------------------------------

export const deleteSkillsApi = async (skillIds: string[]) => {
  try {
    const response = await axiosClient.delete("/v1/skills", {
      data: { skill_ids: skillIds },
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// DELETE /v1/skills/categories — single or bulk category delete
// -----------------------------------------------------------------------------

export const deleteCategoriesApi = async (categoryIds: string[]) => {
  try {
    const response = await axiosClient.delete("/v1/skills/categories", {
      data: { category_ids: categoryIds },
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// -----------------------------------------------------------------------------
// POST /v1/skills/bulk-import — multipart file import
//
// House-style exception: unlike the other calls here, a validation failure
// (400 + data.errors) and the plain-message 400s are RETURNED as a structured
// result rather than thrown — per-row errors are first-class UI content the
// drawer renders. Only network / 5xx / unexpected shapes go to apiErrorHandler.
// -----------------------------------------------------------------------------

const mapBulkImportFailure = (body: {
  message?: string;
  data?: { errors?: SkillsBulkImportRowError[] };
}): SkillsBulkImportResult => {
  const errors = body.data?.errors;
  if (errors && errors.length > 0) {
    return { ok: false, kind: "validation", errors };
  }
  return {
    ok: false,
    kind: "message",
    message: body.message ?? "Import failed. Please try again.",
  };
};

export const bulkImportSkillsApi = async (
  file: File,
): Promise<SkillsBulkImportResult> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await axiosClient.post(
      "/v1/skills/bulk-import",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return { ok: true, data: resData.data as SkillsBulkImportSuccess };
    }
    return mapBulkImportFailure(resData);
  } catch (error) {
    const axiosError = error as AxiosError<{
      success?: boolean;
      message?: string;
      data?: { errors?: SkillsBulkImportRowError[] };
    }>;
    const body = axiosError.response?.data;
    if (body && body.success === false) {
      return mapBulkImportFailure(body);
    }
    return apiErrorHandler(error as AxiosError | AxiosResponse) as never;
  }
};
